import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle, ArrowLeft, LayoutDashboard, Users, Bell, CalendarCheck } from "lucide-react";
import EventbriteEmbed from "@/components/EventbriteEmbed";

export interface RegistrationFlowResult {
  registrationId: string;
  isTeamParticipant: boolean;
  firstName?: string;
  lastName?: string;
  email: string;
  attendeeType?: string;
}

interface EventbriteRegistrationFlowProps {
  onComplete: (result: RegistrationFlowResult) => void;
  onCancel?: () => void;
  eventId?: string;
  embedded?: boolean;
  /** If true, shows Eventbrite embed first (for users who haven't registered yet) */
  showEventbriteFirst?: boolean;
}

type FlowStep = 
  | "checking-auth"
  | "eventbrite-checkout"
  | "account-prompt"
  | "otp-sent"
  | "lookup-registration"
  | "complete"
  | "error";

const EventbriteRegistrationFlow: React.FC<EventbriteRegistrationFlowProps> = ({
  onComplete,
  onCancel,
  eventId,
  embedded = false,
  showEventbriteFirst = false,
}) => {
  const [step, setStep] = useState<FlowStep>("checking-auth");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [eventbriteCompleted, setEventbriteCompleted] = useState(false);

  // Check auth state on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setIsLoggedIn(true);
        setUserEmail(session.user.email || null);
        setEmail(session.user.email || "");
        
        // Logged-in users go straight to Eventbrite or registration lookup
        if (showEventbriteFirst) {
          setStep("eventbrite-checkout");
        } else {
          // Check if they already have a registration
          await lookupRegistration(session.user.email || "");
        }
      } else {
        setIsLoggedIn(false);
        // CHANGED: Guests start with Eventbrite checkout (event-first approach)
        setStep("eventbrite-checkout");
      }
    } catch (err) {
      console.error("Error checking auth:", err);
      // CHANGED: Default to Eventbrite checkout on error too
      setStep("eventbrite-checkout");
    }
  };

  const handleSendOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Try to sign up with auto-confirm enabled - creates user AND signs them in immediately
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password: crypto.randomUUID(), // Random password (they'll use magic link for future logins)
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (signUpError) {
        // If user already exists, send OTP and show continue option
        if (signUpError.message.includes("already registered") || signUpError.message.includes("already been registered")) {
          const { error: otpError } = await supabase.auth.signInWithOtp({
            email,
            options: { shouldCreateUser: false },
          });
          if (otpError) throw otpError;
          
          // Show OTP sent message but with continue option
          setStep("otp-sent");
          toast.success("Login link sent! You can also continue below while waiting.");
          return;
        }
        throw signUpError;
      }

      // User created and signed in immediately (with auto-confirm enabled)
      if (data.user) {
        await ensureUserApproved(data.user.id, email);
        setIsLoggedIn(true);
        setUserEmail(email);
        toast.success("Account created!");
        // Immediately look up their registration
        await lookupRegistration(email);
      }
    } catch (err: any) {
      console.error("Error creating account:", err);
      setError(err.message || "Failed to create account");
      toast.error(err.message || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  // Link registration to user, sync profile data, and auto-follow associated challenge
  const linkRegistrationToUser = async (registrationEmail: string, userId: string) => {
    try {
      // 1. Link registration to user AND fetch all contact data
      const { data: registration, error } = await supabase
        .from("event_registrations")
        .update({ user_id: userId })
        .eq("email", registrationEmail.toLowerCase())
        .is("user_id", null)
        .select("event_id, first_name, last_name, phone, interest_category, attendee_type")
        .maybeSingle();

      if (error) {
        console.error("Failed to link registration to user:", error);
        return;
      }

      // 2. Sync profile data from Eventbrite registration
      if (registration) {
        const profileData: Record<string, any> = {
          id: userId,
          updated_at: new Date().toISOString(),
        };

        // Sync first_name and last_name separately
        if (registration.first_name) {
          profileData.first_name = registration.first_name;
          profileData.display_name = registration.last_name
            ? `${registration.first_name} ${registration.last_name}`
            : registration.first_name;
        }
        if (registration.last_name) {
          profileData.last_name = registration.last_name;
        }
        if (registration.phone) {
          profileData.phone = registration.phone;
        }
        if (registration.interest_category) {
          profileData.interests = [registration.interest_category];
        }
        if (registration.attendee_type?.toLowerCase().includes('participant')) {
          profileData.open_to_networking = true;
        }

        const { error: upsertError } = await supabase
          .from("contributor_profiles")
          .upsert(profileData as any, { onConflict: 'id' });

        if (upsertError) {
          console.error("Failed to sync profile data:", upsertError);
        } else {
          console.log("Synced Eventbrite data to contributor_profiles:", profileData);
        }
      }

      // 3. If registration has an event_id, auto-follow linked challenge
      if (registration?.event_id) {
        const { data: challenge } = await supabase
          .from("challenges")
          .select("id")
          .eq("event_id", registration.event_id)
          .maybeSingle();

        if (challenge) {
          await supabase.from("user_follows").upsert({
            user_id: userId,
            entity_type: "challenge",
            entity_id: challenge.id
          }, { onConflict: "user_id,entity_type,entity_id" });
          console.log("Auto-followed challenge:", challenge.id);
        }
      }
    } catch (err) {
      console.error("Error linking registration to user:", err);
    }
  };

  const ensureUserApproved = async (userId: string, userEmail: string) => {
    try {
      // Use the SECURITY DEFINER function to approve the user if they have an Eventbrite registration
      const { error: rpcError } = await supabase.rpc('approve_eventbrite_registrant', {
        p_user_id: userId,
        p_email: userEmail
      });
      
      if (rpcError) {
        console.error("Error calling approve_eventbrite_registrant:", rpcError);
      }

      // Also link registration to user
      await linkRegistrationToUser(userEmail, userId);
    } catch (err) {
      console.error("Error ensuring user approved:", err);
      // Don't throw - this is not critical for the flow
    }
  };

  const lookupRegistration = async (lookupEmail: string) => {
    setStep("lookup-registration");
    setLoading(true);
    setError(null);

    try {
      const response = await supabase.functions.invoke("lookup-eventbrite-registration", {
        body: { email: lookupEmail },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to lookup registration");
      }

      const data = response.data;

      if (!data.success) {
        // No registration found - need to go to Eventbrite
        setStep("eventbrite-checkout");
        return;
      }

      // If logged in, ensure user is approved and link registration
      if (isLoggedIn) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Call ensureUserApproved to handle the timing issue where
          // handle_new_user trigger runs before registration exists
          await ensureUserApproved(session.user.id, lookupEmail);
          // Note: ensureUserApproved already calls linkRegistrationToUser internally
        }
      }

      // Registration found - complete the flow
      setStep("complete");
      onComplete({
        registrationId: data.registrationId,
        isTeamParticipant: data.isTeamParticipant,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || lookupEmail,
        attendeeType: data.attendeeType,
      });
    } catch (err: any) {
      console.error("Error looking up registration:", err);
      
      // If 404, they need to register on Eventbrite
      if (err.message?.includes("no_registration_found") || err.message?.includes("404")) {
        setStep("eventbrite-checkout");
        return;
      }

      setError(err.message || "Failed to look up registration");
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const handleEventbriteComplete = async () => {
    setEventbriteCompleted(true);
    
    // If user is already logged in, look up their registration directly
    if (isLoggedIn && userEmail) {
      await lookupRegistration(userEmail);
    } else {
      // CHANGED: For guests, show account creation prompt after Eventbrite checkout
      setStep("account-prompt");
    }
  };

  const handleSkipAccountCreation = () => {
    // Complete without account - allow continuing to team flow
    // Since they completed Eventbrite, assume they're a participant
    onComplete({
      registrationId: "",
      isTeamParticipant: true,
      email: email || "guest",
      attendeeType: "pending",
    });
    toast.info("You can create an account anytime to officially manage your team membership.");
  };

  const handleResendOtp = async () => {
    await handleSendOtp();
  };

  // Render based on current step
  const renderStep = () => {
    switch (step) {
      case "checking-auth":
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Preparing registration...</p>
          </div>
        );

      case "eventbrite-checkout":
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold font-display text-foreground">Register for the Event</h2>
            <p className="text-muted-foreground">
              Complete your registration using Eventbrite below. Scroll to see all ticket options — you can join as a general attendee, participant, or sponsor.
            </p>
            </div>

            <EventbriteEmbed
              mode="inline"
              iframeHeight={1200}
              eventId={eventId}
              onOrderComplete={handleEventbriteComplete}
            />

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full border-border text-foreground hover:bg-muted"
              onClick={handleEventbriteComplete}
            >
              I've already registered on Eventbrite
            </Button>
          </div>
        );

      case "account-prompt":
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold">You're Registered!</h3>
              <p className="text-sm text-muted-foreground">
                Create a BuildWithChrist account to unlock more features
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <LayoutDashboard className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Personal Dashboard</p>
                  <p className="text-xs text-muted-foreground">Track your registration and event details</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Join or Form a Team</p>
                  <p className="text-xs text-muted-foreground">Collaborate with others on challenges</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CalendarCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Track Your Progress</p>
                  <p className="text-xs text-muted-foreground">See your registration status and updates</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Event Reminders</p>
                  <p className="text-xs text-muted-foreground">Get notified about important updates</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email (use the same email you registered with)</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Create My Account"
                )}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <Button 
              variant="ghost" 
              className="w-full text-muted-foreground"
              onClick={handleSkipAccountCreation}
            >
              Skip for now
            </Button>
          </div>
        );

      case "otp-sent":
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">Login Link Sent!</h3>
              <p className="text-sm text-muted-foreground">
                We sent a login link to <span className="font-medium text-foreground">{email}</span>
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-center">
                You can continue to team selection now. The login link will still work when you're ready to access your full dashboard later.
              </p>
              <p className="text-xs text-center text-muted-foreground">
                Don't see the email? Check your spam or junk folder.
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}

            {/* Primary action: Continue to team selection */}
            <Button 
              className="w-full" 
              onClick={() => lookupRegistration(email)}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Finding your registration...
                </>
              ) : (
                "Continue to Team Selection"
              )}
            </Button>

            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={handleResendOtp} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Didn't receive it? Send again"
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep("account-prompt");
                  setError(null);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Use a different email
              </Button>
            </div>
          </div>
        );

      case "lookup-registration":
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Finding your registration...</p>
          </div>
        );

      case "complete":
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold">Registration Complete!</h3>
            <p className="text-sm text-muted-foreground text-center">
              Your registration has been confirmed.
            </p>
          </div>
        );

      case "error":
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-destructive">Something went wrong</h3>
              <p className="text-sm text-muted-foreground">
                {error || "We couldn't complete your registration. Please try again."}
              </p>
            </div>

            <Button 
              className="w-full" 
              onClick={() => {
                setError(null);
                setStep("eventbrite-checkout");
              }}
            >
              Try Again
            </Button>

            {onCancel && (
              <Button variant="ghost" className="w-full" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={embedded ? "" : "bg-card border border-border rounded-xl p-6"}>
      {renderStep()}
    </div>
  );
};

export default EventbriteRegistrationFlow;
